import { FieldArray, getIn, useFormikContext } from 'formik'
import { Fragment, ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from 'components/v2/Row'
import { INewPartnerStep2 } from 'features/Partners/types'

import { CancelIcon } from 'components/Icons/CancelIcon'

const messages = defineMessages({
  bankNamePlaceholder: {
    id: 'partners.form.bankName.placeholder',
    defaultMessage: 'Enter a bank name'
  },
  clabeNumberPlaceholder: {
    id: 'partners.form.clabeNumber.placeholder',
    defaultMessage: 'Enter CLABE'
  },
  phonePlaceholder: {
    id: 'partners.form.phone.placeholder',
    defaultMessage: 'Enter a phone number'
  },
  secondaryPhonePlaceholder: {
    id: 'partners.form.secondaryPhone.placeholder',
    defaultMessage: 'Enter a secondary phone number'
  },

  emailPlaceholder: {
    id: 'partners.form.email.placeholder',
    defaultMessage: 'Enter an email'
  },
  secondaryEmailPlaceholder: {
    id: 'partners.form.secondaryEmail.placeholder',
    defaultMessage: 'Enter a secondary email'
  },
  contactNamePlaceholder: {
    id: 'partners.form.contactName.placeholder',
    defaultMessage: 'Enter a contact name'
  },

  namePlaceholder: {
    id: 'partners.form.name.placeholder',
    defaultMessage: 'Enter the partner name'
  },
  cityPlaceholder: {
    id: 'partners.form.city.placeholder',
    defaultMessage: 'Enter a city name'
  }
})

export const PartnerDataContactsCard = (): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<INewPartnerStep2>()

  return (
    <FormLayout.Card small>
      <FieldArray
        name="contacts"
        render={(arrayHelpers): ReactElement => (
          <>
            <FormLayout.CardHeader>
              <Heading.H4>
                <FormattedMessage id="partnerData.new.contact" defaultMessage="Contact" />
              </Heading.H4>
              <Button
                kind="secondary"
                type="button"
                onClick={(): void =>
                  arrayHelpers.push({
                    name: '',
                    phone: '',
                    secondaryPhone: '',
                    email: '',
                    secondaryEmail: ''
                  })
                }
              >
                <FormattedMessage id="partnerData.new.form.addContact" defaultMessage="Add contact" />
              </Button>
            </FormLayout.CardHeader>
            {values.contacts.map((contact, index) => (
              <Fragment key={index}>
                {index > 0 ? (
                  <FormLayout.CardHeader>
                    <Heading.H4>
                      <FormattedMessage
                        id="partnerData.new.form.nextContact.title"
                        defaultMessage="Contact {idx}"
                        values={{
                          idx: index + 1
                        }}
                      />
                    </Heading.H4>
                    <FormLayout.DeleteItemAction onClick={(): void => arrayHelpers.remove(index)}>
                      <CancelIcon />
                    </FormLayout.DeleteItemAction>
                  </FormLayout.CardHeader>
                ) : null}

                <Row noMargin>
                  <Column width="50%">
                    <Input
                      id={`contacts.${index}.name`}
                      value={contact.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={intl.formatMessage(messages.contactNamePlaceholder)}
                      touched={getIn(touched, `contacts.${index}.name`)}
                      errors={getIn(errors, `contacts.${index}.name`)}
                      label={<FormattedMessage id="companyClient.contactName.label" defaultMessage="CONTACT NAME" />}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Input
                      id={`contacts.${index}.phone`}
                      type="tel"
                      value={contact.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={intl.formatMessage(messages.phonePlaceholder)}
                      touched={getIn(touched, `contacts.${index}.phone`)}
                      errors={getIn(errors, `contacts.${index}.phone`)}
                      label={<FormattedMessage id="companyClient.phone.label" defaultMessage="Phone" />}
                    />
                  </Column>
                  <Column>
                    <Input
                      id={`contacts.${index}.secondaryPhone`}
                      type="tel"
                      value={contact.secondaryPhone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={intl.formatMessage(messages.secondaryPhonePlaceholder)}
                      touched={getIn(touched, `contacts.${index}.secondaryPhone`)}
                      errors={getIn(errors, `contacts.${index}.secondaryPhone`)}
                      label={
                        <FormattedMessage id="companyClient.secondaryPhone.label" defaultMessage="Secondary Phone" />
                      }
                      optional
                    />
                  </Column>
                </Row>
                <Row>
                  <Column width="50%">
                    <Input
                      id={`contacts.${index}.email`}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={contact.email}
                      placeholder={intl.formatMessage(messages.emailPlaceholder)}
                      touched={getIn(touched, `contacts.${index}.email`)}
                      errors={getIn(errors, `contacts.${index}.email`)}
                      label={<FormattedMessage id="companyClient.email.label" defaultMessage="E MAIL" />}
                    />
                  </Column>
                  <Column width="50%">
                    <Input
                      id={`contacts.${index}.secondaryEmail`}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={contact.secondaryEmail}
                      placeholder={intl.formatMessage(messages.secondaryEmailPlaceholder)}
                      touched={getIn(touched, `contacts.${index}.secondaryEmail`)}
                      errors={getIn(errors, `contacts.${index}.secondaryEmail`)}
                      label={
                        <FormattedMessage id="companyClient.secondaryEmail.label" defaultMessage="SECONDARY EMAIL" />
                      }
                      optional
                    />
                  </Column>
                </Row>
              </Fragment>
            ))}
          </>
        )}
      ></FieldArray>
    </FormLayout.Card>
  )
}
